import React from 'react';
import { NavLink, Outlet, useParams, Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { useTenant } from '../../context/TenantContext';

/**
 * TenantShell — layout shell for the /t/:tenantSlug route tree.
 *
 * For platform staff/admin: Navbar + a compact horizontal community sub-nav bar.
 * For community members: Navbar + a sidebar sidenav (same as before).
 */
const TenantShell = () => {
  const { tenantSlug } = useParams();
  const { viewTenant, viewerRole, isViewModuleEnabled, isPlatformStaff } = useTenant();

  const base = `/t/${tenantSlug}`;

  const nav = [
    { to: `${base}/dashboard`, label: 'Dashboard' },
    { to: '/coms', label: '💬 Coms Hub' },
    isViewModuleEnabled('consultation')  && { to: `${base}/consultation`,   label: 'Consultation' },
    isViewModuleEnabled('communityNews') && { to: `${base}/community-news`, label: 'Community News' },
    isViewModuleEnabled('communityData') && { to: `${base}/community-data`, label: 'Community Data' },
    isViewModuleEnabled('harvesting')    && { to: `${base}/harvesting`,     label: '🌿 Harvesting' },
    isViewModuleEnabled('landValues')    && { to: `${base}/land-values`,    label: '🗺️ Land Values' },
    isViewModuleEnabled('landUnits')     && { to: `${base}/land-units`,     label: '🗻 Land Units' },
    (viewerRole === 'community_admin' || isPlatformStaff)
      && { to: `${base}/community-admin`, label: 'Community Admin' },
  ].filter(Boolean);

  const displayName = viewTenant?.branding?.displayName || viewTenant?.name || tenantSlug;

  const banner = viewTenant?.branding?.bannerText && (
    <div className="shell-community-banner">{viewTenant.branding.bannerText}</div>
  );

  // ── CIP staff/admin: top Navbar + horizontal community sub-nav ──────────────
  if (isPlatformStaff) {
    return (
      <div className="shell shell--tenant">
        <Navbar />
        {banner}
        <nav className="tenant-subnav" aria-label="Community navigation">
          <Link to="/dashboard" className="tenant-subnav__back">← Platform</Link>
          <span className="tenant-subnav__divider" />
          <span className="tenant-subnav__label">{displayName}</span>
          {nav.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === `${base}/dashboard`}
              className={({ isActive }) =>
                isActive ? 'tenant-subnav__link active' : 'tenant-subnav__link'
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <main className="shell-content--full">
          <Outlet />
        </main>
      </div>
    );
  }

  // ── Community members: Navbar + sidebar sidenav ─────────────────────────────
  return (
    <div className="shell shell--tenant">
      <Navbar />
      {banner}
      <div className="shell-body">
        <nav className="shell-sidenav" aria-label="Community navigation">
          <span className="shell-sidenav__heading">{displayName}</span>
          <ul>
            {nav.map(({ to, label }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === `${base}/dashboard`}
                  className={({ isActive }) =>
                    isActive
                      ? 'shell-sidenav__link shell-sidenav__link--active'
                      : 'shell-sidenav__link'
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="shell-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default TenantShell;
